import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Download, ChevronDown, ChevronUp,
  RefreshCw, Database, Trash2, Plus, AlertTriangle, Building2, Check, ChevronsUpDown
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { useCurrency } from '@/lib/useCurrency';
import { useUser } from '@/lib/UserContext';
import { uploadInChunks, describeUploadError } from '@/lib/chunkedUpload';
import { toast } from 'sonner';

const DATA_COLUMNS = {
  required: [
    { name: 'Supplier Name', description: 'Name of the vendor/supplier', analytics: 'All dashboards' },
    { name: 'Spend Amount', description: 'Transaction amount (numeric, no currency symbols)', analytics: 'All dashboards' },
    { name: 'Date of Transaction', description: 'Invoice or transaction date (e.g., 2024-01-15). Maps to Invoice Date', analytics: 'Trend Analysis, Comparative Analysis, P2P Analytics' },
    { name: 'Categories of Spend', description: 'Primary spend category (e.g., Professional Services). Maps to Category Level 1', analytics: 'Treemap, Category Console, Filters, Comparative Analysis' },
  ],
  category: [
    { name: 'Category Level 1', description: 'Primary spend category (e.g., Professional Services)', analytics: 'Treemap, Category Console, Filters' },
    { name: 'Category Level 2', description: 'Sub-category (e.g., Venues)', analytics: 'Category hierarchy drill-down' },
    { name: 'Category Level 3', description: 'Tertiary category (e.g., Non-Exam Venue Hire)', analytics: 'Category Console detail' },
    { name: 'Category Level 4', description: 'Most granular category', analytics: 'Category Console drill-down' },
  ],
  supplier: [
    { name: 'Supplier Name (Normalized)', description: 'Standardized uppercase supplier name for dedup', analytics: 'Supplier consolidation' },
    { name: 'New Supplier', description: 'Flag: Yes/No — is this a new supplier?', analytics: 'New supplier tracking' },
    { name: 'Supplier Code', description: 'Unique supplier ID from ERP', analytics: 'Supplier identification' },
    { name: 'Supplier City', description: 'Supplier physical location city', analytics: 'Supplier geo analysis' },
    { name: 'Supplier Country', description: 'Supplier country of origin', analytics: 'Supplier country breakdown' },
    { name: 'Risk Ratings', description: 'Supplier risk: Low / Medium / High', analytics: 'Risk Dashboard, Supplier scoring' },
  ],
  invoice: [
    { name: 'Invoice Number', description: 'Unique invoice reference ID', analytics: 'Duplicate detection, Invoice KPIs' },
    { name: 'Invoice Date', description: 'Date invoice was issued (YYYY-MM-DD). Combined with Payment Terms to derive due date.', analytics: 'Invoice Aging, P2P cycle times, YoY' },
    { name: 'Invoice Entry Date', description: 'Date invoice was entered/released in ERP', analytics: 'GRN-to-Invoice cycle, processing time' },
    { name: 'Invoice Line Number', description: 'Line item number within invoice', analytics: 'Line-level tracking' },
    { name: 'Invoice Line Description', description: 'Description of the invoiced item. Feeds ML classification.', analytics: 'ML classification, Detailed reports' },
  ],
  financial: [
    { name: 'Currency', description: 'Transaction currency code (e.g., GBP, USD, PKR). Triggers FX normalisation.', analytics: 'Currency Analytics, Multi-currency FX normalisation' },
    { name: 'GL Code', description: 'General ledger account code', analytics: 'GL code reporting' },
    { name: 'GL Description', description: 'GL account description text', analytics: 'Financial categorization' },
    { name: 'Tax Code', description: 'Tax classification code', analytics: 'Tax analysis' },
    { name: 'Name for Value Added Tax', description: 'VAT description text', analytics: 'VAT breakdown' },
    { name: 'Payment Terms', description: 'Payment terms code (e.g., B30N, NET30, 30 Days Credit)', analytics: 'Invoice Aging, Payment terms distribution' },
    { name: 'Payment Terms Description', description: 'Human-readable payment terms text', analytics: 'Payment terms tooltips' },
    { name: 'Payment Date', description: 'Date payment was made (blank = unpaid/overdue)', analytics: 'Invoice Aging buckets, Payment cycle analysis' },
    { name: 'Contracted Spend', description: 'Is this spend under contract? (Yes/No)', analytics: 'Maverick spend, Compliance score' },
    { name: 'Order Quantity', description: 'Quantity ordered (numeric)', analytics: 'Unit price analysis, Price Variance' },
    { name: 'Unit Price', description: 'Price per unit (numeric). Unlocks price benchmarking.', analytics: 'Price Variance, Data Quality score' },
  ],
  time: [
    { name: 'Fiscal Month Name', description: 'Fiscal month name (e.g., January)', analytics: 'Monthly trend charts' },
    { name: 'Fiscal Year', description: 'Fiscal year (e.g., 2024)', analytics: 'Year filter, YoY comparison' },
    { name: 'Fiscal Quarter No', description: 'Fiscal quarter (e.g., Q1, Q2)', analytics: 'Quarter filter, QoQ trends' },
  ],
  location: [
    { name: 'Office Location', description: 'Office or site location code', analytics: 'Department/location filters' },
    { name: 'Country', description: 'Transaction country name', analytics: 'Spend by country, Geo mapping' },
    { name: 'Country Code', description: 'ISO country code (e.g., GB, PK)', analytics: 'Map visualization' },
    { name: 'Region', description: 'Geographic region (e.g., EMEA, South Asia)', analytics: 'Regional comparison' },
    { name: 'Longitude', description: 'Geographic longitude (numeric)', analytics: 'Geo bubble map' },
    { name: 'Latitude', description: 'Geographic latitude (numeric)', analytics: 'Geo bubble map' },
  ],
  po: [
    { name: 'PO Number', description: 'Purchase order reference number', analytics: 'PO coverage, P2P tracking' },
    { name: 'PO Creation Date', description: 'Date PO was created (YYYY-MM-DD)', analytics: 'PR-to-PO cycle, PO-to-GRN cycle' },
    { name: 'PO Line Number', description: 'PO line item number', analytics: 'Line-level analysis' },
    { name: 'PO Order Type', description: 'Type: Standard, Framework, etc.', analytics: 'PO type distribution' },
    { name: 'PO Group', description: 'PO group code', analytics: 'PO group reporting' },
    { name: 'PO Group Name', description: 'PO group description text', analytics: 'PO group labels' },
    { name: 'PO Need By Date', description: 'Required delivery date (YYYY-MM-DD)', analytics: 'Delivery tracking' },
    { name: 'PO Delivery Date', description: 'Actual delivery date (YYYY-MM-DD)', analytics: 'Delivery analysis' },
    { name: 'PO Line Description', description: 'PO line item description', analytics: 'Detailed reports' },
    { name: 'Buyer Name', description: 'Name/ID of the buyer', analytics: 'Buyer performance, P2P filters' },
    { name: 'PO Creator Name', description: 'Name of PO creator', analytics: 'Creator performance' },
    { name: 'PO Receipt Number', description: 'Receipt reference number', analytics: 'Receipt tracking' },
    { name: 'GRN Date', description: 'Date goods were received (YYYY-MM-DD)', analytics: '3-way match, PO-to-GRN & GRN-to-Invoice cycles' },
    { name: 'PO Approver', description: 'Name of PO approver', analytics: 'Approval workflow' },
  ],
  pr: [
    { name: 'PR Number', description: 'Purchase requisition reference number', analytics: 'PR coverage, PR-to-PO conversion' },
    { name: 'PR Creation Date', description: 'Date PR was created (YYYY-MM-DD)', analytics: 'PR-to-PO cycle time' },
    { name: 'PR Line Number', description: 'PR line item number', analytics: 'Line-level tracking' },
    { name: 'PR Type', description: 'Type: Standard, Urgent, etc.', analytics: 'PR type analysis' },
    { name: 'PR Need By Date', description: 'Required date (YYYY-MM-DD)', analytics: 'Urgency tracking' },
    { name: 'PR Creator', description: 'Name of PR creator', analytics: 'Creator analysis' },
    { name: 'PR Requestor', description: 'Name of person who requested the PR', analytics: 'Requestor tracking' },
  ],
  organization: [
    { name: 'SBU', description: 'Strategic business unit code', analytics: 'SBU spend breakdown, filters' },
    { name: 'Department', description: 'Department / function, e.g. IT, Finance, HR (older files: "Business Unit" column)', analytics: 'Spend by Department, Department filter' },
    { name: 'Profit Centre', description: 'Profit center code', analytics: 'Profit center reporting' },
    { name: 'Profit Centre Description', description: 'Profit center name', analytics: 'Profit center labels' },
  ],
  material: [
    { name: 'Material Code', description: 'Material/item code from ERP', analytics: 'Material tracking' },
    { name: 'Material Description', description: 'Material/item description text', analytics: 'Product descriptions' },
  ],
  classification: [
    { name: 'Addressability', description: 'Addressable / Non-Addressable spend flag', analytics: 'Spend addressability' },
  ],
};

const ADMIN_ONLY_TIP = 'Only Org Admins and Super Admins can replace or delete data';

export default function UploadData() {
  const { symbol: cs } = useCurrency();
  const currentUser = useUser();
  const canDestroy = (currentUser?.roles || []).some((r) => r === 'super_admin' || r === 'org_admin');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [uploadMode, setUploadMode] = useState('add'); // 'add' or 'replace'
  const [dataStats, setDataStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Due Diligence state
  const [ddFile, setDdFile] = useState(null);
  const [ddUploading, setDdUploading] = useState(false);
  const [ddResult, setDdResult] = useState(null);
  const [ddStats, setDdStats] = useState(null);
  const [ddLoadingStats, setDdLoadingStats] = useState(true);
  const [ddUploadMode, setDdUploadMode] = useState('add'); // 'add' or 'replace'
  // Industry state
  const [industry, setIndustry] = useState('');
  const [industries, setIndustries] = useState([]);
  const [industryOpen, setIndustryOpen] = useState(false);
  const [savingIndustry, setSavingIndustry] = useState(false);

  const fetchIndustry = useCallback(async () => {
    try {
      const [indRes, listRes] = await Promise.all([
        api.get('/benchmark/industry'),
        api.get('/benchmark/industries'),
      ]);
      setIndustry(indRes.data.industry || '');
      setIndustries(listRes.data.industries || []);
    } catch (e) { console.warn('[benchmark] industry lookup failed:', e); }
  }, []);

  const handleIndustryChange = async (value) => {
    setIndustry(value);
    setIndustryOpen(false);
    setSavingIndustry(true);
    try {
      await api.put('/benchmark/industry', { industry: value });
      toast.success(`Industry set to ${value}`);
    } catch {
      toast.error('Failed to save industry');
    } finally {
      setSavingIndustry(false);
    }
  };

  const fetchDataStats = useCallback(async () => {
    try {
      const response = await api.get('/data/stats');
      setDataStats(response.data);
    } catch (error) {
      console.error('Failed to load data stats');
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const fetchDdStats = useCallback(async () => {
    try {
      const response = await api.get('/suppliers/risk-data/external');
      setDdStats({ record_count: response.data?.length || 0 });
    } catch {
      setDdStats({ record_count: 0 });
    } finally {
      setDdLoadingStats(false);
    }
  }, []);

  useEffect(() => {
    fetchDataStats();
    fetchDdStats();
    fetchIndustry();
  }, [fetchDataStats, fetchDdStats, fetchIndustry]);

  const handleDdFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = ['.csv', '.xlsx', '.xls'];
      const isValid = validTypes.some(type => selectedFile.name.toLowerCase().endsWith(type));
      if (!isValid) {
        toast.error('Please upload a CSV or Excel file');
        return;
      }
      setDdFile(selectedFile);
      setDdResult(null);
    }
  };

  const handleDdUpload = async () => {
    if (!ddFile) {
      toast.error('Please select a file first');
      return;
    }
    setDdUploading(true);
    const formData = new FormData();
    formData.append('file', ddFile);
    try {
      const response = await api.post(`/suppliers/risk-data/upload?mode=${ddUploadMode}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDdResult({ success: true, data: response.data });
      const d = response.data || {};
      const msg = ddUploadMode === 'replace'
        ? `Replaced: ${d.deleted || 0} deleted, ${d.inserted || 0} inserted`
        : `Due diligence data uploaded: ${d.inserted || 0} inserted, ${d.updated || 0} updated`;
      toast.success(msg);
      fetchDdStats();
    } catch (error) {
      setDdResult({ success: false, error: error.response?.data?.detail || 'Upload failed' });
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setDdUploading(false);
    }
  };

  const downloadDdTemplate = async () => {
    try {
      const response = await api.get('/suppliers/risk-data/template', { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'due_diligence_template.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Due diligence template downloaded');
    } catch {
      toast.error('Failed to download template');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = ['.csv', '.xlsx', '.xls'];
      const isValid = validTypes.some(type => selectedFile.name.toLowerCase().endsWith(type));
      if (!isValid) {
        toast.error('Please upload a CSV or Excel file');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    if (uploadMode === 'replace' && dataStats?.transaction_count > 0) {
      const confirmed = window.confirm(
        `This will DELETE all existing data (${dataStats.transaction_count.toLocaleString()} transactions, ${dataStats.supplier_count} suppliers) and replace it with the new file. Are you sure?`
      );
      if (!confirmed) return;
    }

    setUploading(true);
    setResult(null);
    setUploadProgress({ stage: 'uploading', pct: 0, detail: 'Preparing upload...' });

    try {
      const job_id = await uploadInChunks(file, uploadMode, (sent, total) => setUploadProgress({
        stage: 'uploading',
        pct: Math.round((sent / total) * 5),
        detail: `Uploading... ${(sent / 1048576).toFixed(1)} / ${(total / 1048576).toFixed(1)} MB`,
      }));
      setUploadProgress({ stage: 'processing', pct: 5, detail: 'File received. Processing started...' });
      pollUploadStatus(job_id);
    } catch (error) {
      const msg = describeUploadError(error);
      setResult({ success: false, error: msg });
      toast.error(msg);
      setUploading(false);
    }
  };

  const pollUploadStatus = (jobId) => {
    const poll = async () => {
      try {
        const response = await api.get(`/upload/status/${jobId}`);
        const { status, progress, detail, result: jobResult, error: jobError } = response.data;

        setUploadProgress({ stage: status, pct: progress, detail });

        if (status === 'completed') {
          setResult({ success: true, data: jobResult });
          toast.success(jobResult.message);
          setUploading(false);
          fetchDataStats();
          return;
        }

        if (status === 'failed') {
          setResult({ success: false, error: jobError || 'Processing failed' });
          toast.error(jobError || 'Processing failed');
          setUploading(false);
          return;
        }

        setTimeout(poll, 2000);
      } catch (err) {
        if (err.response?.status === 404) {
          setResult({ success: false, error: 'Upload job not found — please retry the upload.' });
          setUploading(false);
          return;
        }
        setTimeout(poll, 3000);
      }
    };
    setTimeout(poll, 1500);
  };

  const handleDeleteAllData = async () => {
    setDeleting(true);
    try {
      const response = await api.delete('/data');
      toast.success(response.data.message);
      setShowDeleteConfirm(false);
      fetchDataStats(); // Refresh stats after delete
      setResult(null);
    } catch (error) {
      toast.error('Failed to delete data');
    } finally {
      setDeleting(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/template/download', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'spendlens_data_template.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Complete template downloaded — 65 columns with 10 sample rows');
    } catch {
      // Fallback: generate header-only template
      const headers = Object.values(DATA_COLUMNS).flat().map(c => c.name).join(',');
      const blob = new Blob([headers + '\n'], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'spendlens_data_template.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Template headers downloaded');
    }
  };

  const ColumnSection = ({ title, columns, sectionKey, color }) => {
    const isExpanded = expandedSections[sectionKey];
    return (
      <div className="border rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection(sectionKey)}
          className="w-full flex items-center justify-between p-3 bg-muted/50 hover:bg-muted transition-colors"
        >
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={color}>{columns.length}</Badge>
            <span className="font-medium text-sm">{title}</span>
          </div>
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {isExpanded && (
          <div className="p-3 space-y-2">
            {columns.map((col, idx) => (
              <div key={idx} className="flex items-start gap-2 text-sm">
                <span className="font-mono text-xs bg-muted px-2 py-1 rounded whitespace-nowrap">{col.name}</span>
                <div className="flex-1">
                  <span className="text-muted-foreground">{col.description}</span>
                  {col.analytics && (
                    <span className="text-[10px] text-primary/70 block mt-0.5">Used in: {col.analytics}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="upload-data-page">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold tracking-tight">Upload Data</h1>
        <p className="text-muted-foreground mt-1">Import spend data and supplier due diligence information — powers all analytics, AI insights, ML anomaly detection, and PDS scoring</p>
      </div>

      <Tabs defaultValue="spend" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="spend" data-testid="spend-tab">Spend Data</TabsTrigger>
          <TabsTrigger value="due-diligence" data-testid="dd-tab">Due Diligence</TabsTrigger>
        </TabsList>

        {/* ===== SPEND DATA TAB ===== */}
        <TabsContent value="spend" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
          </div>

      {/* Current Data Stats */}
      <Card className="border-l-4 border-l-primary">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Database className="w-5 h-5" />
            Current Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingStats ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <RefreshCw className="w-4 h-4 animate-spin" />
              Loading...
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <div>
                <p className="text-lg font-bold font-mono">{dataStats?.transaction_count?.toLocaleString() || 0}</p>
                <p className="text-sm text-muted-foreground">Transactions</p>
              </div>
              <div>
                <p className="text-lg font-bold font-mono">{dataStats?.supplier_count?.toLocaleString() || 0}</p>
                <p className="text-sm text-muted-foreground">Suppliers</p>
              </div>
              <div>
                <p className="text-lg font-bold font-mono">{cs}{dataStats?.total_spend?.toLocaleString() || 0}</p>
                <p className="text-sm text-muted-foreground">Total Spend</p>
              </div>
              <div className="ml-auto flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchDataStats}
                  data-testid="refresh-stats-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh
                </Button>
 {dataStats?.transaction_count > 0 && canDestroy && (
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={() => setShowDeleteConfirm(true)}
                    data-testid="delete-all-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete All
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Confirm Delete
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                Are you sure you want to delete <strong>all data</strong>? This will remove:
              </p>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                <li>{dataStats?.transaction_count?.toLocaleString()} transactions</li>
                <li>{dataStats?.supplier_count} suppliers</li>
                <li>{cs}{dataStats?.total_spend?.toLocaleString()} worth of spend data</li>
              </ul>
              <p className="text-sm text-red-600 font-medium">This action cannot be undone!</p>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleDeleteAllData}
                  disabled={deleting}
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? 'Deleting...' : 'Delete All Data'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Upload Section */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Upload File
              </CardTitle>
              <CardDescription>
                Supported formats: CSV, Excel (.xlsx, .xls)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Upload Mode Selection */}
              <div className="space-y-3">
                <label className="text-sm font-medium">Upload Mode</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setUploadMode('add')}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      uploadMode === 'add' 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid="mode-add"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Plus className="w-5 h-5 text-green-600" />
                      <span className="font-medium">Add to Existing</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Append new data to your current dataset
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => canDestroy && setUploadMode('replace')}
                    disabled={!canDestroy}
                    title={canDestroy ? undefined : ADMIN_ONLY_TIP}
                    className={`p-4 rounded-lg border-2 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      uploadMode === 'replace' 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid="mode-replace"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <RefreshCw className="w-5 h-5 text-orange-600" />
                      <span className="font-medium">Replace All</span>{!canDestroy && <Badge variant="outline" className="ml-auto text-[10px]" data-testid="replace-admin-only">Admin only</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Delete existing data and upload fresh
                    </p>
                  </button>
                </div>
                {uploadMode === 'replace' && dataStats?.transaction_count > 0 && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-orange-50 border border-orange-200 text-sm">
                    <AlertTriangle className="w-4 h-4 text-orange-600 shrink-0" />
                    <span className="text-orange-800">
                      This will delete {dataStats.transaction_count.toLocaleString()} existing transactions
                    </span>
                  </div>
                )}
              </div>

              <div
                className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary transition-colors cursor-pointer"
                onClick={() => document.getElementById('file-input').click()}
                data-testid="file-drop-zone"
              >
                <input
                  id="file-input"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="file-input"
                />
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                {file ? (
                  <div>
                    <p className="font-medium text-primary">{file.name}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="font-medium">Click to select a file</p>
                    <p className="text-sm text-muted-foreground mt-1">or drag and drop</p>
                  </div>
                )}
              </div>

              {/* Industry Selector */}
              <div className="space-y-2" data-testid="industry-selector">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-primary" />
                  Industry
                  <span className="text-xs text-muted-foreground font-normal">(feeds AI benchmarking)</span>
                </label>
                <Popover open={industryOpen} onOpenChange={setIndustryOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={industryOpen}
                      className="w-full justify-between h-10 font-normal"
                      data-testid="industry-combobox"
                    >
                      {industry || 'Select your industry...'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search industry..." data-testid="industry-search" />
                      <CommandList>
                        <CommandEmpty>No industry found.</CommandEmpty>
                        <CommandGroup>
                          {industries.map((ind) => (
                            <CommandItem
                              key={ind}
                              value={ind}
                              onSelect={() => handleIndustryChange(ind)}
                              data-testid={`industry-option-${ind.replace(/\s+/g, '-').toLowerCase()}`}
                            >
                              <Check className={cn('mr-2 h-4 w-4', industry === ind ? 'opacity-100' : 'opacity-0')} />
                              {ind}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {savingIndustry && <p className="text-xs text-muted-foreground">Saving...</p>}
              </div>

              <Button
                onClick={handleUpload}
                disabled={!file || uploading}
                className={`w-full ${uploadMode === 'replace' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-[#001f5c] hover:bg-[#001845]'}`}
                data-testid="upload-button"
              >
                {uploading ? 'Uploading...' : uploadMode === 'replace' ? 'Replace & Upload' : 'Add & Upload'}
              </Button>

              {/* Upload Progress */}
              {uploadProgress && uploading && (
                <div className="space-y-2" data-testid="upload-progress">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{uploadProgress.detail}</span>
                    <span className="font-mono font-medium">{uploadProgress.pct}%</span>
                  </div>
                  <Progress value={uploadProgress.pct} className="h-2" />
                </div>
              )}

              {result && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-lg ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}
                  data-testid="upload-result"
                >
                  <div className="flex items-start gap-3">
                    {result.success ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                    )}
                    <div className="flex-1">
                      {result.success ? (
                        <>
                          <p className="font-medium text-green-800">{result.data.message}</p>
                          <p className="text-sm text-green-700 mt-1">
                            Processed {result.data.columns_processed} columns, 
                            updated {result.data.suppliers_updated} suppliers
                          </p>
                          {/* ML Training Feedback */}
                          {result.data.ml_results && (result.data.ml_results.classification || result.data.ml_results.anomaly) && (
                            <div className="mt-3 p-3 rounded-lg bg-white border" data-testid="ml-training-feedback">
                              <p className="text-xs font-medium text-muted-foreground mb-2">ML Models</p>
                              <div className="flex flex-wrap gap-2">
                                {result.data.ml_results.classification && (
                                  <span className={`text-xs px-2 py-1 rounded-full border ${
                                    result.data.ml_results.classification.status === 'trained'
                                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                      : 'bg-amber-50 border-amber-200 text-amber-700'
                                  }`}>
                                    Classification: {result.data.ml_results.classification.status === 'trained'
                                      ? `${Math.round((result.data.ml_results.classification.accuracy || 0) * 100)}% accuracy (${result.data.ml_results.classification.samples} samples)`
                                      : 'insufficient data'}
                                  </span>
                                )}
                                {result.data.ml_results.anomaly && (
                                  <span className={`text-xs px-2 py-1 rounded-full border ${
                                    result.data.ml_results.anomaly.status === 'trained'
                                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                      : 'bg-amber-50 border-amber-200 text-amber-700'
                                  }`}>
                                    Anomaly: {result.data.ml_results.anomaly.status === 'trained'
                                      ? `${result.data.ml_results.anomaly.anomalies_found} flagged (${Math.round((result.data.ml_results.anomaly.anomaly_rate || 0) * 100)}%)`
                                      : 'insufficient data'}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                          {/* P2P Field Detection Feedback */}
                          {result.data.p2p_fields_detected && Object.keys(result.data.p2p_fields_detected).length > 0 && (
                            <div className="mt-3 p-3 rounded-lg bg-white border">
                              <p className="text-xs font-medium text-muted-foreground mb-2">P2P Fields Detected</p>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(result.data.p2p_fields_detected).map(([field, count]) => (
                                  <span key={field} className={`text-xs px-2 py-1 rounded-full border ${count > 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-600'}`}>
                                    {field}: {count > 0 ? `${count} rows` : 'empty'}
                                  </span>
                                ))}
                              </div>
                              {['PO Creation Date', 'PR Creation Date', 'GRN / Goods Receipt', 'Invoice Entry/Release Date'].some(
                                f => !result.data.p2p_fields_detected[f] || result.data.p2p_fields_detected[f] === 0
                              ) && (
                                <p className="text-[10px] text-amber-600 mt-2">
                                  Missing P2P fields will limit cycle time analysis, 3-way matching, and GRN coverage on the P2P Analytics page.
                                </p>
                              )}
                            </div>
                          )}
                          {/* Unmapped Columns Warning */}
                          {Array.isArray(result.data.unmapped_columns) && result.data.unmapped_columns.length > 0 && (
                            <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="unmapped-columns-warning">
                              <p className="text-xs font-medium text-amber-800 mb-1">
                                {result.data.unmapped_columns.length} column{result.data.unmapped_columns.length === 1 ? '' : 's'} were not auto-mapped and were ignored
                              </p>
                              <div className="flex flex-wrap gap-1.5 my-2">
                                {result.data.unmapped_columns.slice(0, 20).map((col) => (
                                  <span key={col} data-testid="unmapped-column-chip" className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-amber-300 text-amber-800 font-mono">
                                    {col}
                                  </span>
                                ))}
                                {result.data.unmapped_columns.length > 20 && (
                                  <span className="text-[11px] px-2 py-0.5 text-amber-700">+{result.data.unmapped_columns.length - 20} more</span>
                                )}
                              </div>
                              <p className="text-[10px] text-amber-700">
                                If any of these should drive analytics (e.g. a PR/PO/GRN field, fiscal field, or supplier attribute), rename the CSV header to one of our recognised aliases and re-upload — or share the header with your administrator to add a new alias.
                              </p>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="font-medium text-red-800">{result.error}</p>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </CardContent>
          </Card>

          {/* Quick Tips */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Tips</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <Badge className="bg-[#001f5c] shrink-0">1</Badge>
                <span><strong>Supplier Name</strong>, <strong>Spend Amount</strong>, <strong>Date of Transaction</strong> and <strong>Categories of Spend</strong> are the essential data fields for analytics — all other fields enhance insights further</span>
              </div>
              <div className="flex items-start gap-2">
                <Badge className="bg-[#001f5c] shrink-0">2</Badge>
                <span>Column names are auto-mapped: case-insensitive, spaces/hyphens become underscores</span>
              </div>
              <div className="flex items-start gap-2">
                <Badge className="bg-[#001f5c] shrink-0">3</Badge>
                <span>For <strong>P2P Analytics</strong>: include PO/PR Creation Dates, GRN Date, and Invoice Entry Date</span>
              </div>
              <div className="flex items-start gap-2">
                <Badge className="bg-[#001f5c] shrink-0">4</Badge>
                <span>For <strong>3-Way Matching</strong>: PO Number + GRN Date + Invoice Number must all be present</span>
              </div>
              <div className="flex items-start gap-2">
                <Badge className="bg-[#001f5c] shrink-0">5</Badge>
                <span>For <strong>Invoice Aging</strong>: include Invoice Date + Payment Terms + Payment Date (blank Payment Date = unpaid)</span>
              </div>
              <div className="flex items-start gap-2">
                <Badge className="bg-[#001f5c] shrink-0">6</Badge>
                <span><strong>Contracted Spend</strong> (Yes/No) drives Maverick Spend detection and Compliance scoring</span>
              </div>
              <div className="flex items-start gap-2">
                <Badge className="bg-[#001f5c] shrink-0">7</Badge>
                <span><strong>Currency</strong> column triggers auto FX normalisation for multi-currency reporting</span>
              </div>
              <div className="flex items-start gap-2">
                <Badge className="bg-[#001f5c] shrink-0">8</Badge>
                <span>More populated fields = higher <strong>Data Quality (PDS) score</strong> and better AI insight accuracy</span>
              </div>
              <div className="flex items-start gap-2">
                <Badge className="bg-[#001f5c] shrink-0">9</Badge>
                <span>Dates should be in YYYY-MM-DD format (DD/MM/YYYY also accepted)</span>
              </div>
            </CardContent>
          </Card>

          {/* What happens on upload */}
          <Card className="border-l-4 border-l-emerald-500" data-testid="upload-actions-info">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">What happens when you upload</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <span>Columns are auto-mapped, numeric fields cleaned, and fiscal year/month derived from date fields if missing</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <span>Transactions staged in a safe rollback area before commit — a failed upload leaves your existing data untouched</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <span>Supplier master list is upserted; existing suppliers get updated total spend, risk ratings, and city/country</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <span>ML models (spend classification + anomaly detection) auto-train on your organisation's data once sufficient records exist</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <span>Data Quality (PDS) score is recomputed so AI insights reflect the fresh dataset</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Column Specification */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Data Column Specification
            </CardTitle>
            <CardDescription>
              65 supported columns grouped by function. Each field shows which analytics it enables. Click to expand.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 max-h-[600px] overflow-y-auto">
            <ColumnSection 
              title="Required Fields" 
              columns={DATA_COLUMNS.required} 
              sectionKey="required"
              color="bg-red-100 text-red-800 border-red-300"
            />
            <ColumnSection 
              title="Category Hierarchy" 
              columns={DATA_COLUMNS.category} 
              sectionKey="category"
              color="bg-blue-100 text-blue-800 border-blue-300"
            />
            <ColumnSection 
              title="Supplier Information" 
              columns={DATA_COLUMNS.supplier} 
              sectionKey="supplier"
              color="bg-purple-100 text-purple-800 border-purple-300"
            />
            <ColumnSection 
              title="Invoice Details" 
              columns={DATA_COLUMNS.invoice} 
              sectionKey="invoice"
              color="bg-green-100 text-green-800 border-green-300"
            />
            <ColumnSection 
              title="Financial Fields" 
              columns={DATA_COLUMNS.financial} 
              sectionKey="financial"
              color="bg-yellow-100 text-yellow-800 border-yellow-300"
            />
            <ColumnSection 
              title="Time Dimensions" 
              columns={DATA_COLUMNS.time} 
              sectionKey="time"
              color="bg-orange-100 text-orange-800 border-orange-300"
            />
            <ColumnSection 
              title="Location/Geography" 
              columns={DATA_COLUMNS.location} 
              sectionKey="location"
              color="bg-teal-100 text-teal-800 border-teal-300"
            />
            <ColumnSection 
              title="Purchase Order (PO)" 
              columns={DATA_COLUMNS.po} 
              sectionKey="po"
              color="bg-indigo-100 text-indigo-800 border-indigo-300"
            />
            <ColumnSection 
              title="Purchase Requisition (PR)" 
              columns={DATA_COLUMNS.pr} 
              sectionKey="pr"
              color="bg-pink-100 text-pink-800 border-pink-300"
            />
            <ColumnSection 
              title="Organization Structure" 
              columns={DATA_COLUMNS.organization} 
              sectionKey="organization"
              color="bg-cyan-100 text-cyan-800 border-cyan-300"
            />
            <ColumnSection 
              title="Material Details" 
              columns={DATA_COLUMNS.material} 
              sectionKey="material"
              color="bg-lime-100 text-lime-800 border-lime-300"
            />
            <ColumnSection 
              title="Classification" 
              columns={DATA_COLUMNS.classification} 
              sectionKey="classification"
              color="bg-gray-100 text-gray-800 border-gray-300"
            />
          </CardContent>
        </Card>
      </div>
        </TabsContent>

        {/* ===== DUE DILIGENCE TAB ===== */}
        <TabsContent value="due-diligence" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button variant="outline" onClick={downloadDdTemplate} data-testid="download-dd-template-btn">
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
          </div>

          {/* DD Current Data Stats */}
          <Card className="border-l-4 border-l-amber-500">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Database className="w-5 h-5" />
                Current Due Diligence Data
              </CardTitle>
            </CardHeader>
            <CardContent>
              {ddLoadingStats ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Loading...
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-lg font-bold font-mono" data-testid="dd-record-count">{ddStats?.record_count || 0}</p>
                    <p className="text-sm text-muted-foreground">Supplier Records</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchDdStats} data-testid="refresh-dd-stats-btn">
                    <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* DD Upload Section */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Upload Due Diligence File
                  </CardTitle>
                  <CardDescription>
                    Upload supplier risk and compliance data (CSV or Excel). Use <strong>Add to Existing</strong> to upsert by supplier name, or <strong>Replace All</strong> to wipe and reload from scratch.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Upload Mode Selection (mirrors spend upload) */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium">Upload Mode</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setDdUploadMode('add')}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          ddUploadMode === 'add'
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid="dd-mode-add"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Plus className="w-5 h-5 text-green-600" />
                          <span className="font-medium">Add to Existing</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Append new records; existing suppliers get updated
                        </p>
                      </button>
                      <button
                        type="button"
                        onClick={() => canDestroy && setDdUploadMode('replace')}
                    disabled={!canDestroy}
                    title={canDestroy ? undefined : ADMIN_ONLY_TIP}
                        className={`p-4 rounded-lg border-2 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                          ddUploadMode === 'replace'
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid="dd-mode-replace"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <RefreshCw className="w-5 h-5 text-orange-600" />
                          <span className="font-medium">Replace All</span>{!canDestroy && <Badge variant="outline" className="ml-auto text-[10px]" data-testid="replace-admin-only">Admin only</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Delete existing due-diligence data and upload fresh
                        </p>
                      </button>
                    </div>
                    {ddUploadMode === 'replace' && ddStats?.record_count > 0 && (
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-orange-50 border border-orange-200 text-sm">
                        <AlertTriangle className="w-4 h-4 text-orange-600 shrink-0" />
                        <span className="text-orange-800">
                          This will delete {ddStats.record_count.toLocaleString()} existing due-diligence record(s)
                        </span>
                      </div>
                    )}
                  </div>

                  <div
                    className="border-2 border-dashed rounded-lg p-8 text-center hover:border-amber-500 transition-colors cursor-pointer"
                    onClick={() => document.getElementById('dd-file-input').click()}
                    data-testid="dd-file-drop-zone"
                  >
                    <input
                      id="dd-file-input"
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleDdFileChange}
                      className="hidden"
                      data-testid="dd-file-input"
                    />
                    <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    {ddFile ? (
                      <div>
                        <p className="font-medium text-amber-600">{ddFile.name}</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {(ddFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="font-medium">Click to select a file</p>
                        <p className="text-sm text-muted-foreground mt-1">or drag and drop</p>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={handleDdUpload}
                    disabled={!ddFile || ddUploading}
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    data-testid="dd-upload-button"
                  >
                    {ddUploading ? 'Uploading...' : 'Upload Due Diligence Data'}
                  </Button>

                  {ddResult && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-4 rounded-lg ${ddResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}
                      data-testid="dd-upload-result"
                    >
                      <div className="flex items-start gap-3">
                        {ddResult.success ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                        )}
                        <div>
                          {ddResult.success ? (
                            <>
                              <p className="font-medium text-green-800">Due diligence data uploaded successfully</p>
                              <p className="text-sm text-green-700 mt-1">
                                {ddResult.data.inserted} new records inserted, {ddResult.data.updated} existing records updated ({ddResult.data.total} total processed)
                              </p>
                            </>
                          ) : (
                            <p className="font-medium text-red-800">{ddResult.error}</p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </CardContent>
              </Card>

              {/* DD Quick Tips */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quick Tips</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <Badge className="bg-amber-600 shrink-0">1</Badge>
                    <span><strong>Supplier Name</strong> is used to match records — ensure names match your spend data</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge className="bg-amber-600 shrink-0">2</Badge>
                    <span><strong>Credit Score</strong> should be 0-100 (higher = better creditworthiness)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge className="bg-amber-600 shrink-0">3</Badge>
                    <span><strong>ESG Rating</strong> accepts A+, A, B+, B, C, D, F grades</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge className="bg-amber-600 shrink-0">4</Badge>
                    <span><strong>Financial Health Score</strong> should be 0-100 (higher = healthier)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge className="bg-amber-600 shrink-0">5</Badge>
                    <span>Uploading again for the same supplier will <strong>update</strong> the existing record</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* DD Column Specification */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5" />
                  Due Diligence Columns
                </CardTitle>
                <CardDescription>
                  Required and optional columns for due diligence data upload.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="border rounded-lg overflow-hidden">
                  <div className="p-3 bg-red-50 border-b">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-red-100 text-red-800 border-red-300">Required</Badge>
                      <span className="font-medium text-sm">Supplier Name</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Must match supplier names in your spend data. Case-insensitive matching.</p>
                  </div>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <div className="p-3 bg-amber-50 border-b">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className="bg-amber-100 text-amber-800 border-amber-300">4</Badge>
                      <span className="font-medium text-sm">Risk & Compliance Fields</span>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-start gap-2 text-sm">
                        <span className="font-mono text-xs bg-muted px-2 py-1 rounded whitespace-nowrap">Credit Score</span>
                        <span className="text-muted-foreground">Numeric (0-100). Supplier's creditworthiness score from rating agencies.</span>
                      </div>
                      <div className="flex items-start gap-2 text-sm">
                        <span className="font-mono text-xs bg-muted px-2 py-1 rounded whitespace-nowrap">ESG Rating</span>
                        <span className="text-muted-foreground">Grade (A+ to F). Environmental, Social, and Governance rating.</span>
                      </div>
                      <div className="flex items-start gap-2 text-sm">
                        <span className="font-mono text-xs bg-muted px-2 py-1 rounded whitespace-nowrap">Financial Health Score</span>
                        <span className="text-muted-foreground">Numeric (0-100). Overall financial stability assessment.</span>
                      </div>
                      <div className="flex items-start gap-2 text-sm">
                        <span className="font-mono text-xs bg-muted px-2 py-1 rounded whitespace-nowrap">Risk Notes</span>
                        <span className="text-muted-foreground">Free text. Any additional risk observations or flags.</span>
                      </div>
                    </div>
                  </div>
                </div>

                <Card className="bg-muted/30">
                  <CardContent className="pt-4 pb-3">
                    <p className="text-sm font-medium mb-2">How it feeds into dashboards:</p>
                    <ul className="text-xs text-muted-foreground space-y-1.5">
                      <li>Credit, ESG, and Health scores appear on <strong>supplier cards</strong> in Supplier Management</li>
                      <li>Composite Risk Score is calculated: 40% internal + 60% external data</li>
                      <li><strong>Risk Dashboard</strong> uses this data for the scatter matrix, at-risk table, and AI recommendations</li>
                      <li>The <strong>Supplier Due Diligence</strong> count on the Risk Dashboard reflects uploaded records</li>
                    </ul>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
